import { createClient } from '@supabase/supabase-js';
import { ChartData } from '../types/charts.js';

const supabaseUrl = 'https://xexbydxogiszzdmxbkjm.supabase.co';
const supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InhleGJ5ZHhvZ2lzenpkbXhia2ptIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzgwMTQyNTIsImV4cCI6MjA1MzU5MDI1Mn0.V1CRZPu4XJtsudIih0mcD9zjxx0m3QnnTZW7wOwKgNo';

console.log('Initializing Supabase client with:', { supabaseUrl, supabaseAnonKey });

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Test Supabase connection
(async () => {
  try {
    const { error } = await supabase.from('charts').select('count').single();
    if (error) {
      console.error('Failed to connect to Supabase:', error);
    } else {
      console.log('Successfully connected to Supabase');
    }
  } catch (error: unknown) {
    console.error('Error testing Supabase connection:', error);
  }
})();

export interface StoredChartData extends ChartData {
  id: string;
  name: string;
  last_updated: string;
}

export async function upsertChartData(chartId: string, chartName: string, chartData: ChartData) {
  try {
    console.log('Upserting chart data:', { chartId, chartName, date: chartData.date });
    
    // First, update the charts table
    const { error: chartError } = await supabase
      .from('charts')
      .upsert({
        id: chartId,
        name: chartName,
        date: chartData.date,
        data: chartData.data,
        last_updated: new Date().toISOString()
      });

    if (chartError) {
      console.error('Supabase chart upsert error:', chartError);
      throw chartError;
    }

    // Then, insert/update entries in the entries table
    const entries = chartData.data.map(entry => ({
      chart_id: chartId,
      name: entry.name,
      artist: entry.artist,
      rank: entry.rank,
      last_week_rank: entry.last_week_rank,
      peak_rank: entry.peak_rank,
      weeks_on_chart: entry.weeks_on_chart,
      image: entry.image,
      preview_url: entry.previewUrl,
      date: chartData.date
    }));

    // Delete old entries for this chart and date
    const { error: deleteError } = await supabase
      .from('entries')
      .delete()
      .eq('chart_id', chartId)
      .eq('date', chartData.date);

    if (deleteError) {
      console.error('Supabase entries delete error:', deleteError);
      throw deleteError;
    }

    // Insert new entries
    const { error: entriesError } = await supabase
      .from('entries')
      .insert(entries);

    if (entriesError) {
      console.error('Supabase entries insert error:', entriesError);
      throw entriesError;
    }
  } catch (error) {
    console.error('Error in upsertChartData:', error);
    throw error;
  }
}

export async function getChartData(chartId: string) {
  try {
    console.log('Getting chart data for:', chartId);
    const { data, error } = await supabase
      .from('charts')
      .select('*')
      .eq('id', chartId)
      .single();

    if (error) {
      console.error('Supabase select error:', error);
      throw error;
    }

    return data as StoredChartData;
  } catch (error) {
    console.error('Error in getChartData:', error);
    throw error;
  }
}

export async function getAllCharts() {
  try {
    console.log('Getting all charts');
    const { data, error } = await supabase
      .from('charts')
      .select('id, name, date, last_updated')
      .order('name');

    if (error) {
      console.error('Supabase select error:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in getAllCharts:', error);
    throw error;
  }
} 